/**
 * #429 / #435 — Storage-safe Web Storage access (localStorage + sessionStorage).
 *
 * `getItem/setItem/removeItem` THROW when storage is denied (Safari private mode,
 * blocked cookies, quota exceeded) — and accessing `window.localStorage` /
 * `window.sessionStorage` itself can throw in some contexts. An unguarded access
 * in a UI handler (dismissing a modal, logging out, prefilling a form) propagates
 * the throw and can leave the surface stuck. These helpers degrade to a no-op /
 * null so a storage failure never breaks the flow — the worst case is a flag or
 * snapshot that isn't persisted.
 */

export type StorageKind = 'local' | 'session';

function store(kind: StorageKind = 'local'): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return (kind === 'session' ? window.sessionStorage : window.localStorage) ?? null;
  } catch {
    return null;
  }
}

export function safeGetItem(key: string, kind: StorageKind = 'local'): string | null {
  try {
    return store(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function safeSetItem(key: string, value: string, kind: StorageKind = 'local'): void {
  try {
    store(kind)?.setItem(key, value);
  } catch {
    // storage denied / quota — ignore; the flow must not depend on persistence.
  }
}

export function safeRemoveItem(key: string, kind: StorageKind = 'local'): void {
  try {
    store(kind)?.removeItem(key);
  } catch {
    // storage denied — ignore.
  }
}
